import React, { useState, useCallback, useEffect } from 'react';
import { CheckCircle2, XCircle, Loader2, RefreshCw, Zap, ZapOff } from 'lucide-react';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import SecretField from './SecretField';
import { formatRelativeTime as sharedFormatRelativeTime } from '@/lib/match/formatters';

/**
 * AppDirectSection
 *
 * Fields (Wave 2 rewrite — session-cookie auth, dual base URLs):
 *   auth.cookie (SECRET, multiline — the operator pastes their whole browser
 *     session in any shape; the server parses it via
 *     services/match/cookie_paste.py and derives auth.xsrf_token from the
 *     same paste, which is why there is no XSRF input here anymore)
 *   base_url_sandbox, base_url_prod
 *   active_env ("sandbox" | "prod")
 *   live_post_enabled (bool switch)
 *   multipass_enabled (bool)
 *   defaults.{contract_type_id_new, contract_term_id_12mo/24mo/36mo, sales_agent_id}
 *   cached_lookups summary + refresh
 *
 * Removed: base_url (single), bearer_token — replaced by auth block above.
 */

// Shared with the board's product-catalog timestamp — see lib/match/formatters.
// The shared helper returns null for a missing value; "never" is this panel's
// own wording for a lookup cache that has never been populated.
const formatRelativeTime = (iso) => sharedFormatRelativeTime(iso) ?? 'never';

export default function AppDirectSection({ value = {}, onChange, readOnly }) {
  const auth = value.auth ?? {};
  const defaults = value.defaults ?? {};

  const [testState, setTestState] = useState('idle');
  const [testMessage, setTestMessage] = useState('');
  const [testLatency, setTestLatency] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookups, setLookups] = useState(value.cached_lookups ?? null);
  const [lookupsFetchedAt, setLookupsFetchedAt] = useState(value.lookups_fetched_at ?? null);

  // Re-render every minute so the relative "Fetched X ago" text actually
  // ages — without this it renders once and sits at "just now" forever.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setClockTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  function patch(changes) {
    onChange({ ...value, ...changes });
  }

  function patchAuth(field, val) {
    onChange({ ...value, auth: { ...auth, [field]: val } });
  }

  function patchDefault(field, val) {
    onChange({ ...value, defaults: { ...defaults, [field]: Number(val) || 0 } });
  }

  async function handleTest() {
    setTestState('testing');
    setTestMessage('');
    setTestLatency(null);
    try {
      const res = await api.post(`${API_URL}/api/match/settings/test-appdirect`);
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setTestState('success');
        setTestMessage(data.message ?? 'Connected');
        setTestLatency(data.latency_ms ?? null);
        setTimeout(() => setTestState('idle'), 10000);
      } else {
        setTestState('error');
        setTestMessage(data.message ?? data.detail ?? 'Test failed');
      }
    } catch {
      setTestState('error');
      setTestMessage('Could not reach backend');
    }
  }

  // Refresh Lookups is the step that makes the Product dropdown work at all —
  // resolve_provider_id_for_session reads the cached providers list and never
  // calls AppDirect live, so an unsynced cache means provider_id resolves to 0
  // and products are never fetched. Until 2026-07-30 this handler acted only
  // on res.ok with no else branch: a stale cookie produced a spinner, then
  // silence, with "Not yet synced" still on screen and no reason given. On the
  // one button that fixes the outage, that was the worst possible place for it.
  const handleRefreshLookups = useCallback(async () => {
    setLookupLoading(true);
    setLookupError('');
    try {
      const res = await api.get(`${API_URL}/api/match/settings/appdirect-lookups`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLookupError(
          data?.detail ||
            `Lookup sync failed (HTTP ${res.status}). If AppDirect rejected the ` +
              'session, paste a fresh one above, Save, then try again.',
        );
        return;
      }
      const total = Object.values(data || {}).reduce(
        (n, v) => n + (Array.isArray(v) ? v.length : 0),
        0,
      );
      if (!total) {
        // 200 with nothing in it: auth was accepted but every table came back
        // empty. Distinct from a failure, and the operator must not read a
        // green tick into it.
        setLookupError(
          'AppDirect returned no lookup data. The session may have expired — ' +
            'paste a fresh one above, Save, run Test Connection, then retry.',
        );
        return;
      }
      setLookups(data);
      setLookupsFetchedAt(new Date().toISOString());
    } catch {
      setLookupError('Could not reach the backend to sync lookups.');
    } finally {
      setLookupLoading(false);
    }
  }, []);

  const lastTest = value.last_test;

  return (
    <div className="space-y-6">
      {/* ── Session Cookie Auth ── */}
      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-boss-text-muted">
          Session Cookie Auth
        </div>
        <p className="text-xs text-boss-text-muted">
          AppDirect uses session cookies — not a bearer token. Log in to AppDirect, then paste your
          session below. The server pulls out what it needs and discards the rest, so you can paste
          the whole thing without tidying it up. The XSRF token is read from the same paste — there
          is no second field to fill in.
        </p>
        <p className="text-xs text-boss-text-muted">
          Any of these work: the{' '}
          <code className="font-mono bg-boss-bg-elevated px-1 rounded">Cookie</code> request header
          (DevTools → Network → any request), <em>Copy as cURL</em>, or the rows from DevTools →
          Application → Cookies. Sessions are short-lived — if lookups stop working, paste a fresh
          one. A paste with no{' '}
          <code className="font-mono bg-boss-bg-elevated px-1 rounded">JSESSIONID</code> is rejected
          rather than saved, so you will know immediately if you copied the wrong thing.
        </p>

        <div className="space-y-1">
          <label htmlFor="ad-cookie" className="block text-xs font-medium text-boss-text-muted">
            Session
            <span className="ml-1 font-normal text-boss-text-muted">(encrypted)</span>
          </label>
          <SecretField
            id="ad-cookie"
            value={auth.cookie ?? ''}
            onChange={(v) => patchAuth('cookie', v)}
            placeholder="Paste your whole AppDirect session here — extra cookies and formatting are fine"
            multiline
            disabled={readOnly}
          />
        </div>
      </div>

      {/* ── API Environments ── */}
      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-boss-text-muted">
          API Environments
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="ad-url-sandbox" className="block text-xs font-medium text-boss-text-muted">
              Sandbox Base URL
            </label>
            <input
              id="ad-url-sandbox"
              type="url"
              className="input-field w-full text-sm"
              placeholder="https://catalogsandbox.byappdirect.com/api/provider-sales/aso-stage/v1"
              value={value.base_url_sandbox ?? ''}
              onChange={(e) => patch({ base_url_sandbox: e.target.value })}
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="ad-url-prod" className="block text-xs font-medium text-boss-text-muted">
              Production Base URL
            </label>
            <input
              id="ad-url-prod"
              type="url"
              className="input-field w-full text-sm"
              placeholder="https://catalog.appdirect.com/api/provider-sales/aso/v1"
              value={value.base_url_prod ?? ''}
              onChange={(e) => patch({ base_url_prod: e.target.value })}
              disabled={readOnly}
            />
          </div>
        </div>

        {/* Active environment toggle */}
        <fieldset>
          <legend className="text-xs font-medium text-boss-text-muted mb-2">
            Active Environment
          </legend>
          <div className="flex gap-2" role="radiogroup" aria-label="Active environment">
            {[
              { id: 'sandbox', label: 'Sandbox' },
              { id: 'prod', label: 'Production' },
            ].map(({ id: envId, label }) => (
              <button
                key={envId}
                type="button"
                role="radio"
                aria-checked={value.active_env === envId}
                disabled={readOnly}
                onClick={() => !readOnly && patch({ active_env: envId })}
                className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 ${
                  value.active_env === envId
                    ? envId === 'prod'
                      ? 'border-[var(--boss-warning-border)] bg-boss-status-warn-bg text-boss-status-warn'
                      : 'border-boss-primary/60 bg-boss-primary/10 text-boss-text'
                    : 'border-boss-border bg-boss-bg-elevated text-boss-text-muted hover:border-boss-primary/40 hover:text-boss-text'
                } ${readOnly ? 'opacity-60 cursor-default' : 'cursor-pointer'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {value.active_env === 'prod' && (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-boss-status-warn-bg border border-[var(--boss-warning-border)] px-2.5 py-0.5 text-xs text-boss-status-warn font-medium">
              All API calls will target Production AppDirect
            </p>
          )}
        </fieldset>

        {/* Test Connection */}
        <div className="flex items-center justify-between gap-4 pt-1">
          <div aria-live="polite" className="flex items-center gap-1.5 text-xs min-w-0">
            {testState === 'idle' && (
              <span className="text-boss-text-muted">Save changes first, then test.</span>
            )}
            {testState === 'testing' && (
              <span className="flex items-center gap-1.5 text-boss-text-muted">
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> Testing...
              </span>
            )}
            {testState === 'success' && (
              <span className="flex items-center gap-1.5 text-boss-status-success">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                {testMessage}{testLatency != null ? ` (${testLatency}ms)` : ''}
              </span>
            )}
            {testState === 'error' && (
              <span className="flex items-center gap-1.5 text-boss-status-danger min-w-0 truncate">
                <XCircle className="h-3.5 w-3.5 shrink-0" /> {testMessage}
              </span>
            )}
          </div>
          <button
            type="button"
            className="btn-secondary text-sm flex items-center gap-1.5 shrink-0"
            disabled={testState === 'testing' || readOnly}
            onClick={handleTest}
          >
            Test Connection
          </button>
        </div>

        {lastTest && (
          <div className="text-xs text-boss-text-muted">
            Last tested:{' '}
            {lastTest.tested_at ? new Date(lastTest.tested_at).toLocaleString() : 'unknown'}
            {lastTest.status === 'success' && lastTest.latency_ms != null
              ? ` — ${lastTest.latency_ms}ms`
              : ''}
          </div>
        )}
      </div>

      {/* ── Push Configuration ── */}
      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-boss-text-muted">
          Push Configuration
        </div>

        {/* Live Post toggle */}
        <div
          className={`glass-card flex items-center justify-between gap-4 p-4 rounded-lg border transition-colors ${
            value.live_post_enabled
              ? 'border-[var(--boss-success-border)] bg-boss-status-success-bg'
              : 'border-boss-border'
          }`}
        >
          <div className="flex items-start gap-3 flex-1">
            {value.live_post_enabled ? (
              <Zap className="h-4 w-4 text-boss-status-success mt-0.5 shrink-0" />
            ) : (
              <ZapOff className="h-4 w-4 text-boss-text-muted mt-0.5 shrink-0" />
            )}
            <div>
              <div className="text-sm font-medium text-boss-text">
                Live Post {value.live_post_enabled ? 'ON' : 'OFF'}
              </div>
              <div className="text-xs text-boss-text-muted mt-0.5">
                {value.live_post_enabled
                  ? 'Reviewer actions will push live to AppDirect. Ensure the cookie is fresh and the connection tests green.'
                  : 'Reviewer actions produce a preview only — nothing is sent to AppDirect.'}
              </div>
              {value.live_post_enabled && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-boss-status-warn-bg border border-[var(--boss-warning-border)] px-2.5 py-0.5 text-xs text-boss-status-warn font-medium">
                  Live posting will push to AppDirect for real
                </div>
              )}
            </div>
          </div>
          {!readOnly && (
            <button
              type="button"
              role="switch"
              aria-checked={value.live_post_enabled}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                value.live_post_enabled
                  ? 'bg-[var(--boss-success)] border-[var(--boss-success)]'
                  : 'bg-boss-bg-elevated border-boss-border'
              }`}
              onClick={() => patch({ live_post_enabled: !value.live_post_enabled })}
            >
              <span
                className={`block h-3.5 w-3.5 rounded-full bg-boss-text shadow transition-transform ${
                  value.live_post_enabled ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </button>
          )}
        </div>

        {/* Multipass */}
        <label className={`flex items-start gap-3 ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-boss-border accent-boss-primary"
            checked={value.multipass_enabled ?? false}
            onChange={(e) => patch({ multipass_enabled: e.target.checked })}
            disabled={readOnly}
          />
          <div>
            <div className="text-sm font-medium text-boss-text">
              Multi-commission-account orders (multipass)
            </div>
            <div className="text-xs text-boss-text-muted mt-0.5">
              Enable if your AppDirect tenant uses multipass commission tracking.
              Check with your AppDirect account manager. When in doubt, leave off.
            </div>
          </div>
        </label>
      </div>

      {/* ── AppDirect Enum Defaults ── */}
      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-boss-text-muted">
          AppDirect Enum Defaults
        </div>
        <p className="text-xs text-boss-text-muted">
          Fallback AppDirect FK IDs used when cached lookup tables don't yield a match.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <NumberField
            id="ad-ct-new"
            label="Contract Type ID (New)"
            value={defaults.contract_type_id_new}
            onChange={(v) => patchDefault('contract_type_id_new', v)}
            disabled={readOnly}
          />
          <NumberField
            id="ad-term-12"
            label="Contract Term ID (12mo)"
            value={defaults.contract_term_id_12mo}
            onChange={(v) => patchDefault('contract_term_id_12mo', v)}
            disabled={readOnly}
          />
          <NumberField
            id="ad-term-24"
            label="Contract Term ID (24mo)"
            value={defaults.contract_term_id_24mo}
            onChange={(v) => patchDefault('contract_term_id_24mo', v)}
            disabled={readOnly}
          />
          <NumberField
            id="ad-term-36"
            label="Contract Term ID (36mo)"
            value={defaults.contract_term_id_36mo}
            onChange={(v) => patchDefault('contract_term_id_36mo', v)}
            disabled={readOnly}
          />
          <NumberField
            id="ad-agent"
            label="Sales Agent ID"
            value={defaults.sales_agent_id}
            onChange={(v) => patchDefault('sales_agent_id', v)}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* ── Lookup Tables ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-boss-text-muted">
              Lookup Tables
            </div>
            <p className="text-xs text-boss-text-muted mt-0.5">
              Cached AppDirect product catalog, contract types, terms, and user directory.
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary text-sm flex items-center gap-1.5"
            disabled={lookupLoading || readOnly}
            onClick={handleRefreshLookups}
          >
            {lookupLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Refresh Lookups
          </button>
        </div>

        {lookupError && (
          <div className="glass-card rounded-lg p-3 text-xs text-boss-status-danger border border-[var(--boss-danger-border)] flex items-start gap-2">
            <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{lookupError}</span>
          </div>
        )}

        {lookups && Object.keys(lookups).length > 0 ? (
          <div className="glass-card rounded-lg p-3 text-xs text-boss-text-muted border border-boss-border">
            <div className="flex items-center gap-1.5 mb-1 text-boss-text font-medium">
              <CheckCircle2 className="h-3.5 w-3.5 text-boss-status-success" />
              Lookups cached
            </div>
            <span>
              {Array.isArray(lookups.products) ? lookups.products.length : 0} products,{' '}
              {Array.isArray(lookups.providers) ? lookups.providers.length : 0} providers,{' '}
              {Array.isArray(lookups.contractTypes) ? lookups.contractTypes.length : 0} contract types,{' '}
              {Array.isArray(lookups.contractTerms) ? lookups.contractTerms.length : 0} contract terms.
            </span>
            {lookupsFetchedAt && (
              <div className="mt-1 text-boss-text-muted">
                Fetched {formatRelativeTime(lookupsFetchedAt)}
              </div>
            )}
          </div>
        ) : (
          <div className="glass-card rounded-lg p-3 text-xs text-boss-text-muted border border-boss-border">
            {lookupsFetchedAt
              ? `Last synced: ${formatRelativeTime(lookupsFetchedAt)}. Click "Refresh Lookups" to update.`
              : 'Not yet synced. Save settings and test the connection first, then click "Refresh Lookups".'}
          </div>
        )}
      </div>
    </div>
  );
}

function NumberField({ id, label, value, onChange, disabled }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-boss-text-muted">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min="0"
        className="input-field w-full text-sm"
        placeholder="0"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}
